import { useForm } from "@inertiajs/react";
import { Check, Plus, X } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TextareaWithCounter } from "@/components/ui/textarea-with-counter";
import { categorySubcategoriesPath } from "@/routes";
import type { Category, Subcategory } from "@/types";

interface SubcategoryFormProps {
  category: Category;
  subcategory?: Subcategory;
  onCancel?: () => void;
  onSuccess?: () => void;
}

export function SubcategoryForm({
  category,
  subcategory,
  onCancel,
  onSuccess,
}: SubcategoryFormProps) {
  const [isOpen, setIsOpen] = useState(!subcategory); // Open by default for new subcategories

  const { data, setData, post, patch, processing, errors, reset } = useForm({
    name: subcategory?.name ?? "",
    description: subcategory?.description ?? "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (subcategory) {
      // Update existing subcategory
      patch(`/categories/${category.id}/subcategories/${subcategory.id}`, {
        preserveScroll: true,
        onSuccess: () => {
          setIsOpen(false);
          onSuccess?.();
        },
      });
    } else {
      // Create new subcategory
      post(categorySubcategoriesPath(category), {
        preserveScroll: true,
        onSuccess: () => {
          reset();
          setIsOpen(false);
          onSuccess?.();
        },
      });
    }
  };

  const handleCancel = () => {
    reset();
    setIsOpen(false);
    onCancel?.();
  };

  if (!isOpen && subcategory) {
    return (
      <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
        <div>
          <h4 className="font-medium">{subcategory.name}</h4>
          {subcategory.description && (
            <p className="text-sm text-muted-foreground">
              {subcategory.description}
            </p>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setIsOpen(true)}>
          Editar
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 p-4 border rounded-lg bg-muted/20"
    >
      <div className="space-y-2">
        <Label htmlFor="name">Nombre de la subcategoría</Label>
        <Input
          id="name"
          value={data.name}
          onChange={(e) => setData("name", e.target.value)}
          placeholder="Ingrese el nombre de la subcategoría"
          disabled={processing}
        />
        {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Descripción (opcional)</Label>
        <TextareaWithCounter
          id="description"
          value={data.description}
          onChange={(e) => setData("description", e.target.value)}
          placeholder="Descripción de la subcategoría"
          disabled={processing}
          rows={3}
          maxLength={500}
          showCharacterCount={true}
        />
        {errors.description && (
          <p className="text-sm text-red-500">{errors.description}</p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={processing} size="sm">
          <Check className="h-4 w-4 mr-2" />
          {subcategory ? "Actualizar" : "Crear"}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={processing}
          size="sm"
        >
          <X className="h-4 w-4 mr-2" />
          Cancelar
        </Button>
      </div>
    </form>
  );
}

interface AddSubcategoryButtonProps {
  category: Category;
  onSuccess?: () => void;
}

export function AddSubcategoryButton({
  category,
  onSuccess,
}: AddSubcategoryButtonProps) {
  const [showForm, setShowForm] = useState(false);

  if (showForm) {
    return (
      <SubcategoryForm
        category={category}
        onCancel={() => setShowForm(false)}
        onSuccess={() => {
          setShowForm(false);
          onSuccess?.();
        }}
      />
    );
  }

  return (
    <Button
      variant="outline"
      onClick={() => setShowForm(true)}
      className="w-full border-dashed"
    >
      <Plus className="h-4 w-4 mr-2" />
      Agregar subcategoría
    </Button>
  );
}
